package com.arena_web.ui.sections.arena.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.ClubDto
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.arena.ArenaEvent
import com.arena_web.ui.sections.arena.EventStatus
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mPaddingTop
import com.arena_web.ui.utils.mWidth
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

@Composable
fun ArenaContentMobile(
    activeTab: String = "Events",
    onTabSelected: (String) -> Unit = {},
    events: List<ArenaEvent>,
    isLoading: Boolean,
    isLoadingMore: Boolean = false,
    onAddEvent: () -> Unit,
    onEventClick: (ArenaEvent) -> Unit = {},
    onLoadMore: () -> Unit = {},
    onMapClick: () -> Unit = {},
    // Map mode — when true the list/grid is hidden (the background map behind
    // this content is the browsing surface) but the action bar stays live so
    // the filters and the Clubs|Events switch keep driving the pins.
    showMap: Boolean = false,
    clubs: List<ClubDto> = emptyList(),
    isLoadingClubs: Boolean = false,
    isLoadingMoreClubs: Boolean = false,
    onLoadMoreClubs: () -> Unit = {},
    onClubClick: (ClubDto) -> Unit = {},
    userLat: Double? = null,
    userLng: Double? = null,
    filterCount: Int = 0,
    onFilterClick: () -> Unit = {},
    sportName: String = "all",
    cities: List<String> = emptyList(),
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String? = null,
    onCitySelected: (String?) -> Unit = {},
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    val t = LocalAppStrings.current

    Box(
        modifier = Modifier
            .fillMaxHeight()
            .styleModifier {
                // Break out of MobilePageLayout's 16px horizontal padding so
                // the backdrop image can bleed edge-to-edge. The inner Column
                // below re-applies the 16px padding for the action bar / cards.
                property("position", "relative")
                // Above the background map's stacking context (map layer is
                // z-index 0) so the action bar + buttons stay visible/clickable
                // in map mode. Leaflet panes use high internal z-indexes, but
                // they're scoped to the map container's own stacking context.
                property("z-index", "1")
                property("width", "calc(100% + 32px)")
                property("margin", "0 -16px")
                // Map mode: this ROOT must be pointer-transparent too — it
                // covers the whole map (z:1) and even a background-less div
                // hit-tests, so without this the map can't be panned/zoomed
                // at all on touch. Controls re-enable pointer-events below.
                if (showMap) property("pointer-events", "none")
            }
    ) {
        // Figma 10567:202821 — sport-specific ambient backdrop. A fixed
        // 487×650 image, center and horizontally over the now-full-width Box
        // so it bleeds past the viewport edges (root Box's overflow:hidden
        // clips the overflow). opacity 0.3 tones the bright pitch art so it
        // melts into the body composite (var(--l-s3, #02040B) + blue tint) and no extra
        // vertical gradient overlay is needed for the header transition.
        // Map mode hides the ambient pitch backdrop — the Leaflet map behind
        // this content is the surface now.
        if (!showMap) Img(
            src = AppIcons.mobileEventBackground(sportName),
            attrs = Modifier.styleModifier {
                // object-fit: contain anchored to the top. The PNG aspect
                // (1086×1448 ≈ 0.75) is less portrait than the mobile content
                // area (≈ 0.51), so cover would crop the sides. Contain shows
                // the whole pitch at full viewport width without distortion;
                // the remainder below it falls back to the body composite.
                property("position", "absolute")
                property("inset", "0")
                property("width", "100%")
                property("height", "100%")
                property("object-fit", "contain")
                // 0 % vertical — pitch lifts up to the container's top edge.
                property("object-position", "center 0%")
                // Light theme shows the art brighter — at the dark-tuned 0.1
                // it reads as murky watermark on the light canvas.
                property("opacity", "var(--l-ambient-art, 0.1)")
                // The PNG carries transparent side margins; invisible on dark
                // (0.1 over black) but they read as odd gutters at the light
                // opacity — scale the art past the viewport edges on light
                // (root Box overflow:hidden clips it). Dark resolves `none`.
                property("transform", "var(--l-ambient-art-scale, none)")
                property("transform-origin", "center top")
                property("pointer-events", "none")
                // Soft top + bottom fade into the body composite. The top fade
                // is extended further down (0 % → 32 %) so the upper third of
                // the pitch reads as darker / more dissolved against the
                // header area; the bottom fade stays softer.
                val mask = "linear-gradient(180deg, " +
                    "transparent 0%, " +
                    "black 32%, " +
                    "black 78%, " +
                    "transparent 100%)"
                property("mask-image", mask)
                property("-webkit-mask-image", mask)
            }.toAttrs()
        )

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(leftRight = 16.px)
                .gap(16.px)
                // Without `position: relative` the Column paints in the static
                // layer, below the absolutely-positioned Img above. Even at
                // opacity 0.3 the Img then visually dims the buttons. Joining
                // the positioned layer puts the Column AFTER Img in document
                // order → content paints over the backdrop.
                .styleModifier {
                    property("position", "relative")
                    // Map mode: the content overlay is transparent to pointer
                    // events on its empty area (below the action bar) so the
                    // Leaflet map behind receives drag/zoom/tap; the action bar
                    // itself re-enables pointer-events.
                    if (showMap) property("pointer-events", "none")
                }
        ) {
        // Compact action bar
        MobileArenaActionBar(
            activeTab = activeTab,
            onTabSelected = onTabSelected,
            onAddEvent = onAddEvent,
            onMapClick = onMapClick,
            showMap = showMap,
            filterCount = filterCount,
            onFilterClick = onFilterClick,
            cities = cities,
            cityFlags = cityFlags,
            selectedCity = selectedCity,
            onCitySelected = onCitySelected,
            selectedDate = selectedDate,
            onDateSelected = onDateSelected
        )

        // Map mode hides the list/grid — the background map is the browsing
        // surface; the action bar above stays live.
        if (!showMap) {
            if (activeTab == "Events") {
                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().flexGrow(1))
                } else if (events.isEmpty()) {
                    MobileEmptyState(onAddEvent = onAddEvent)
                } else {
                    MobileEventGrid(
                        events = events,
                        isLoadingMore = isLoadingMore,
                        onEventClick = onEventClick,
                        onLoadMore = onLoadMore
                    )
                }
            } else {
                if (isLoadingClubs) {
                    Box(
                        modifier = Modifier.fillMaxWidth().flexGrow(1),
                        contentAlignment = Alignment.Center
                    ) {
                        // Intentionally empty — no loading indicator (user pref).
                    }
                } else if (clubs.isEmpty()) {
                    MobileEmptyClubsState()
                } else {
                    MobileClubGrid(
                        clubs = clubs,
                        isLoadingMore = isLoadingMoreClubs,
                        onClubClick = onClubClick,
                        userLat = userLat,
                        userLng = userLng,
                        selectedCity = selectedCity,
                        cityFlags = cityFlags,
                        sportName = sportName
                    )
                }
            }
        }
        }

        // Floating Map + Add actions — bottom-right, stacked clear of the
        // floating bottom-nav pill (mobile-app ArenaTabScreen parity: Add on
        // top — Events tab only — map toggle below). Fixed width = the Add
        // button size so the map toggle stays put when Add appears/hides on
        // the Events↔Clubs switch.
        Column(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("right", "16px")
                    // 120 = the app's BottomNavClearance (ArenaTabScreen) —
                    // the toggle rides visibly clear of the bottom-nav pill.
                    property("bottom", "calc(${com.arena_web.ui.utils.m(120)} + env(safe-area-inset-bottom, 0px))")
                    property("z-index", "2")
                    // Stay tappable in map mode (the root is pointer-transparent).
                    property("pointer-events", "auto")
                }
                .gap(12.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Events only — there is nothing to create on the Clubs tab.
            if (activeTab == "Events") {
                MobileRoundButton(
                    iconSrc = AppIcons.Add,
                    iconSize = 16.px,
                    size = 48.px,
                    solid = true,
                    onClick = onAddEvent
                )
            }
            // Toggles list ⇄ map; the grid glyph reads "back to the list".
            MobileRoundButton(
                iconSrc = if (showMap) AppIcons.GridFilled else AppIcons.Map,
                iconSize = 20.px,
                size = 44.px,
                onClick = onMapClick
            )
        }
    }
}

@Composable
private fun MobileArenaActionBar(
    activeTab: String = "Events",
    onTabSelected: (String) -> Unit = {},
    onAddEvent: () -> Unit,
    onMapClick: () -> Unit = {},
    showMap: Boolean = false,
    filterCount: Int = 0,
    onFilterClick: () -> Unit = {},
    cities: List<String> = emptyList(),
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String? = null,
    onCitySelected: (String?) -> Unit = {},
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Left group — Calendar trigger + Location pill.
        // Date filters events only — clubs browse by city + sport, so the
        // trigger hides on the Clubs tab (mobile-app parity).
        Row(
            modifier = Modifier.gap(8.px)
                // Re-enable pointer events over the controls — the content
                // overlay is `pointer-events: none` in map mode.
                .styleModifier { if (showMap) property("pointer-events", "auto") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (activeTab == "Events") {
                MobileCalendarButton(
                    selectedDate = selectedDate,
                    onDateSelected = onDateSelected
                )
            }
            MobileLocationPill(
                cities = cities,
                cityFlags = cityFlags,
                selectedCity = selectedCity,
                onCitySelected = onCitySelected
            )
        }

        // Right group — Clubs|Events switch only; Map toggle + Add live as
        // bottom-right floating buttons (mobile-app parity).
        Row(
            modifier = Modifier.gap(8.px)
                // Re-enable pointer events over the switch — the content
                // overlay is none in map mode.
                .styleModifier { if (showMap) property("pointer-events", "auto") },
            verticalAlignment = Alignment.CenterVertically
        ) {
            MobileClubsEventsSwitch(
                activeTab = activeTab,
                onTabSelected = onTabSelected
            )
        }
    }
}

@Composable
private fun MobileLocationPill(
    cities: List<String>,
    cityFlags: Map<String, String>,
    selectedCity: String?,
    onCitySelected: (String?) -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }
    val selectedFlag = selectedCity?.let { cityFlags[it].orEmpty() } ?: ""
    val displayValue = selectedCity ?: t.arenaAllCities

    // Stable DOM id so we can detect outside clicks — same trick as the
    // desktop FilterPillDropdown (fixed-position backdrop is trapped by
    // ancestor `transform`s on the action bar).
    val dropdownId = remember { "mlp-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) {
                expanded = false
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        Row(
            modifier = Modifier
                .height(36.px)
                .borderRadius(100.px)
                .padding(leftRight = 14.px)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                t.arenaLocationLabel,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(500)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("white-space", "nowrap") }
            )
            if (selectedFlag.isNotEmpty()) {
                // selectedFlag is a local flag SVG URL — emoji-style 4:3
                // rounded-rect badge (full flag, no crop).
                Box(
                    modifier = Modifier
                        .width(16.px)
                        .height(12.px)
                        .margin(left = 4.px, right = 4.px)
                        .styleModifier {
                            property("background-image", "url(\"$selectedFlag\")")
                            property("background-size", "cover")
                            property("background-position", "center")
                            property("border-radius", "2px")
                        }
                )
            }
            SpanText(
                displayValue,
                modifier = Modifier
                    .fontSize(13.px)
                    .fontWeight(500)
                    .color(sportColors.primary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }

        if (expanded) {
            com.arena_web.ui.components.dropdown.LocationDropdownPanel(
                selectedCity = selectedCity,
                cities = cities,
                cityFlags = cityFlags,
                onSelect = { city ->
                    onCitySelected(city)
                    expanded = false
                },
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(42.px)
                    .left(0.px)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            )
        }
    }
}

@Composable
private fun LocationDropdownRow(
    label: String,
    flag: String,
    isSelected: Boolean,
    primary: KobwebColor,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(8.px)
            .padding(10.px, 12.px)
            .cursor(Cursor.Pointer)
            .gap(6.px)
            .styleModifier {
                if (isSelected) property("background", "rgba(0, 97, 255, 0.12)")
            }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (flag.isNotEmpty()) {
            SpanText(
                flag,
                modifier = Modifier
                    .fontSize(14.px)
                    .styleModifier {
                        property("line-height", "1")
                        property("transform", "translateY(1px)")
                    }
            )
        }
        SpanText(
            label,
            modifier = Modifier
                .fontSize(14.px)
                .fontWeight(500)
                .color(if (isSelected) org.jetbrains.compose.web.css.Color(primary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

// Outside-click handler shared by the mobile dropdown triggers. Same trick as
// the desktop FilterPillDropdown — `position:fixed` backdrop is trapped by an
// ancestor `transform`, so we listen on document instead.
@Composable
private fun OutsideClickHandler(dropdownId: String, expanded: Boolean, onClose: () -> Unit) {
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) onClose()
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }
}

@Composable
private fun MobileCalendarButton(
    selectedDate: String?,
    onDateSelected: (String?) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val dropdownId = remember { "mcal-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    OutsideClickHandler(dropdownId, expanded) { expanded = false }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        MobileRoundButton(
            iconSrc = AppIcons.Calendar,
            onClick = { expanded = !expanded }
        )
        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(42.px)
                    .zIndex(100)
                    .styleModifier {
                        // Width = the DatePicker's intrinsic design size at
                        // current scale (~560 design px). Clamped to viewport
                        // minus 32px gutters so it never overflows.
                        property("--scale", "min(0.62px, calc((100vw - 32px) / 560))")
                        property("width", "calc(560 * var(--scale))")
                        property("box-sizing", "border-box")
                        // var(--l-s2, #09142d) rounded card matching the Location dropdown
                        // (LocationDropdownPanel); the picker is rendered `bare`.
                        property("background", "var(--l-s2, #09142d)")
                        property("border-radius", "24px")
                        // Vertical only — the day strip scrolls edge-to-edge.
                        property("padding", "12px 0")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                        // Center on the viewport regardless of where the
                        // trigger button sits. Wrapper is at viewport-left +
                        // 16px, so to put the dropdown midpoint at 50vw we
                        // anchor left at (50vw - 16px) and pull back 50% of
                        // the dropdown's own width.
                        property("left", "calc(50vw - 16px)")
                        property("transform", "translateX(-50%)")
                    }
            ) {
                com.arena_web.ui.components.forms.DatePicker(
                    selectedDate = selectedDate ?: "",
                    onDateSelected = { date ->
                        onDateSelected(date)
                        expanded = false
                    },
                    bare = true
                )
            }
        }
    }
}

@Composable
private fun MobileRoundButton(
    iconSrc: String,
    iconSize: CSSLengthValue = 16.px,
    size: CSSLengthValue = 36.px,
    solid: Boolean = false,
    onClick: () -> Unit = {}
) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .size(size)
            .flexShrink(0)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .then(
                if (solid) Modifier.backgroundColor(sportColors.primary)
                else Modifier.styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
            ),
        contentAlignment = Alignment.Center
    ) {
        // Icon tint: solid Add → white-on-primary via iconFilterOnPrimary;
        // filter buttons → muted nav blue #B4CCF6 via IconDefaultFilter
        // (matches navigation icon treatment).
        Icon(
            src = iconSrc,
            modifier = Modifier.size(iconSize).styleModifier {
                property(
                    "filter",
                    if (solid) sportColors.iconFilterOnPrimary else com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V
                )
            }
        )
    }
}

@Composable
private fun MobileClubsEventsSwitch(activeTab: String = "Events", onTabSelected: (String) -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .height(36.px)
            .borderRadius(18.px)
            .padding(2.px, 3.px)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Events first — Clubs sits on the RIGHT on mobile (user preference).
        listOf("Events", "Clubs").forEach { tab ->
            val isActive = tab == activeTab
            Box(
                modifier = Modifier
                    .height(30.px)
                    .borderRadius(15.px)
                    .padding(leftRight = 16.px)
                    .then(
                        if (isActive) Modifier.backgroundColor(sportColors.primary)
                        else Modifier
                    )
                    .cursor(Cursor.Pointer)
                    .onClick { onTabSelected(tab) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (tab == "Clubs") t.arenaTabClubs else t.arenaTabEvents,
                    modifier = Modifier
                        .fontSize(13.px)
                        .fontWeight(500)
                        .color(if (isActive) org.jetbrains.compose.web.css.Color(sportColors.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}

private const val MOBILE_GRID_SCROLL_ID = "mobile-event-grid-scroll"
private const val MOBILE_GRID_SCROLL_KEY = "arena_mobile_events_scroll_top"

@Composable
private fun MobileEventGrid(
    events: List<ArenaEvent>,
    isLoadingMore: Boolean = false,
    onEventClick: (ArenaEvent) -> Unit = {},
    onLoadMore: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)
    var restoredScroll by remember { mutableStateOf(false) }

    // Restore the previous scroll position once events are rendered. Keyed
    // on the events-list emptiness flag so it fires exactly once per fresh
    // mount with content — coming back from the event detail page lands here
    // with the same cached list and the user's previous offset is restored.
    LaunchedEffect(events.isNotEmpty()) {
        if (restoredScroll || events.isEmpty()) return@LaunchedEffect
        val saved = kotlinx.browser.window.sessionStorage.getItem(MOBILE_GRID_SCROLL_KEY)
            ?.toDoubleOrNull() ?: return@LaunchedEffect
        // Wait one frame so the cards are actually painted before we set
        // scrollTop — otherwise the container's scrollHeight is still 0 and
        // the assignment is silently clamped to 0.
        kotlinx.browser.window.requestAnimationFrame {
            kotlinx.browser.document.getElementById(MOBILE_GRID_SCROLL_ID)?.scrollTop = saved
            restoredScroll = true
        }
    }

    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(MOBILE_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        var savePending = false
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
            // Throttle the sessionStorage write to one per animation frame —
            // scroll fires per-pixel and synchronous storage writes would
            // stutter mid-scroll on low-end devices.
            if (!savePending) {
                savePending = true
                kotlinx.browser.window.requestAnimationFrame {
                    kotlinx.browser.window.sessionStorage.setItem(
                        MOBILE_GRID_SCROLL_KEY,
                        el.scrollTop.toString()
                    )
                    savePending = false
                }
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Div(
        attrs = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .id(MOBILE_GRID_SCROLL_ID)
            .styleModifier {
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "12px")
                // Cancel the parent Column's 16px gap so the scroll edge sits
                // flush with the action bar; padding-top re-establishes the
                // first-card offset *inside* the scrollable region so the card
                // scrolls past the action bar when the user drags up.
                property("margin-top", "-16px")
                property("padding-top", "16px")
                // NOTE: padding-bottom is ignored by Chrome/Safari at the end
                // of a flex+overflow scroll container — we add an explicit
                // spacer Div below instead so the last card clears the
                // fixed BottomNavBar.

                // Soft fade at the top so cards melt out into the action bar
                // instead of clipping with a hard edge as they scroll up.
                // The fade band sits inside the 16px padding-top + ~12px of
                // the first card, giving a 28px gradient transition.
                val topFade = "linear-gradient(to bottom, transparent 0px, black 28px, black 100%)"
                property("mask-image", topFade)
                property("-webkit-mask-image", topFade)
            }
            .toAttrs()
    ) {
        events.forEach { event ->
            MobileEventCard(
                event = event,
                onClick = { onEventClick(event) }
            )
        }

        if (isLoadingMore) {
            Div(
                attrs = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("text-align", "center")
                        property("padding", "16px")
                    }
                    .toAttrs()
            ) {
                SpanText(
                    t.arenaLoadingMoreEvents,
                    modifier = Modifier.fontSize(14.px).color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }

        // Bottom spacer — lifts the last card well above the fixed BottomNavBar.
        // Real Div (not padding) because flex+overflow:auto containers ignore
        // padding-bottom at the scroll end on Chrome/Safari.
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex", "0 0 200px")
                    property("height", "200px")
                }
                .toAttrs()
        )
    }
}

// =============================================================================
// Mobile Event Card — Figma 10032:208767 (single-column list item)
// =============================================================================

@Composable
private fun MobileEventCard(
    event: ArenaEvent,
    onClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val isCompleted = event.status == EventStatus.COMPLETED || event.status == EventStatus.CANCELED
    val dateColor = if (isCompleted) AppColors.Text.Muted else AppColors.PrimaryWhite
    val metaColor = if (isCompleted) AppColors.Text.Muted else KobwebColor.hex("BED6F5")
    val subtitleColor = if (isCompleted) AppColors.Text.Muted else KobwebColor.hex("B9B9B9")

    // Figma 10032:208803 — card 358×204, border 0.3px solid rgba(255,255,255,0.4),
    // radius 16, overlay gradient 135.88deg (dark→fade), content px-12 with column
    // gap-16, date row + name/location (gap-6) and bottom meta block (gap-4).
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(204.px)
            .flexShrink(0)
            .overflow(Overflow.Hidden)
            .borderRadius(16.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "url('${event.imageUrl}') center/cover no-repeat padding-box, " +
                        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
            }
    ) {
        // Legibility overlay — diagonal dark-to-fade from top-left.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .styleModifier {
                    property(
                        "background",
                        if (isCompleted) "rgba(2, 4, 11, 0.82)"
                        else "linear-gradient(135.88deg, rgba(0, 0, 0, 0.8) 1.93%, rgba(0, 0, 0, 0.5) 53.38%, rgba(153, 153, 153, 0) 94.11%)"
                    )
                }
        )

        // Status badge — top-right (Figma: h28, px16 py8, radius 22).
        Box(
            modifier = Modifier.position(Position.Absolute).top(9.px).right(14.px)
        ) {
            MobileEventBadge(event.status)
        }

        // Content — top-left, 12px horizontal padding, 16px top.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .top(0.px).left(0.px)
                .gap(16.px)
                .styleModifier { property("padding", "16px 12px 6px 12px") }
        ) {
            // Headline group (date + name/location), gap 8 between rows.
            Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
                // Date • time (Figma: 24px Medium)
                Row(
                    modifier = Modifier.gap(8.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        event.date,
                        modifier = Modifier.fontSize(24.px).fontWeight(500).lineHeight(32.px).color(dateColor)
                    )
                    Box(modifier = Modifier.size(4.px).borderRadius(50.percent).backgroundColor(dateColor))
                    SpanText(
                        event.time,
                        modifier = Modifier.fontSize(24.px).fontWeight(500).lineHeight(32.px).color(dateColor)
                    )
                }

                // Name / club / location group (gap 6).
                Column(modifier = Modifier.fillMaxWidth().gap(6.px)) {
                    if (event.title.isNotBlank()) {
                        SpanText(
                            event.title,
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(18.px)
                                .fontWeight(500)
                                .lineHeight(22.px)
                                .color(metaColor)
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                    }
                    if (event.location.isNotBlank()) {
                        SpanText(
                            event.location,
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(12.px)
                                .lineHeight(12.px)
                                .color(subtitleColor)
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                    }
                }
            }

            // Bottom meta — members + format on one row, cost beneath (gap 4).
            Column(modifier = Modifier.gap(4.px)) {
                val membersText = when {
                    event.membersJoined != null && event.membersTotal != null ->
                        t.arenaMembersJoinedTotal(event.membersJoined, event.membersTotal)
                    event.membersJoined != null -> t.arenaMembersCount(event.membersJoined)
                    event.membersTotal != null -> t.arenaMembersCount(event.membersTotal)
                    else -> null
                }
                // Sport chip first — mirrors the desktop card's sport MetaRow
                // (customSportName-aware label via ArenaEvent.sportLabel).
                if (event.sportLabel.isNotBlank()) {
                    MobileMetaChip(AppIcons.ballSmall(event.sportName), event.sportLabel, metaColor)
                }
                Row(
                    modifier = Modifier.gap(18.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (membersText != null) MobileMetaChip(AppIcons.People, membersText, metaColor)
                    if (event.format != null) MobileMetaChip(AppIcons.FootballField, t.arenaFormat(event.format), metaColor)
                }
                if (event.cost != null) MobileMetaChip(AppIcons.Card, event.cost, metaColor)
            }
        }
    }
}

@Composable
private fun MobileMetaChip(iconSrc: String, text: String, color: com.varabyte.kobweb.compose.ui.graphics.Color) {
    Row(
        modifier = Modifier.gap(6.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = iconSrc, modifier = Modifier.size(16.px))
        SpanText(
            text,
            modifier = Modifier.fontSize(12.px).fontWeight(500).lineHeight(12.px).color(color)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

@Composable
private fun MobileEventBadge(status: EventStatus) {
    val t = LocalAppStrings.current
    if (status == EventStatus.IN_PROGRESS) {
        Row(
            modifier = Modifier.gap(6.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(modifier = Modifier.size(6.px).borderRadius(50.percent).backgroundColor(KobwebColor.rgb(242, 85, 85)))
            SpanText(t.arenaStatusLive, modifier = Modifier.fontSize(12.px).fontWeight(500).color(AppColors.PrimaryWhite))
        }
        return
    }

    val bgColor: com.varabyte.kobweb.compose.ui.graphics.Color
    val textColor: com.varabyte.kobweb.compose.ui.graphics.Color
    val label: String
    when (status) {
        EventStatus.NEW -> {
            bgColor = KobwebColor.rgb(0, 97, 255)
            textColor = KobwebColor.rgb(255, 255, 255)
            label = t.arenaStatusNew
        }
        EventStatus.COMPLETED -> {
            bgColor = KobwebColor.rgb(52, 213, 36)
            textColor = KobwebColor.rgb(0, 0, 0)
            label = t.arenaStatusCompleted
        }
        EventStatus.CANCELED -> {
            bgColor = KobwebColor.rgb(242, 85, 85)
            textColor = KobwebColor.rgb(255, 255, 255)
            label = t.arenaStatusCanceled
        }
        EventStatus.IN_PROGRESS -> return
    }

    // Figma 10032:208849 — h28, px16 py8, radius 22, label 14px Regular lh22.
    Box(
        modifier = Modifier
            .height(28.px)
            .backgroundColor(bgColor)
            .borderRadius(22.px)
            .padding(leftRight = 16.px, topBottom = 8.px),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier.fontSize(14.px).lineHeight(22.px).color(textColor)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// =============================================================================
// Mobile Club Grid
// =============================================================================

private const val MOBILE_CLUB_GRID_SCROLL_ID = "mobile-club-grid-scroll"

@Composable
internal fun MobileClubGrid(
    clubs: List<ClubDto>,
    isLoadingMore: Boolean = false,
    onClubClick: (ClubDto) -> Unit = {},
    onLoadMore: () -> Unit = {},
    userLat: Double? = null,
    userLng: Double? = null,
    selectedCity: String? = null,
    cityFlags: Map<String, String> = emptyMap(),
    sportName: String = "all"
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)

    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(MOBILE_CLUB_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Div(
        attrs = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .id(MOBILE_CLUB_GRID_SCROLL_ID)
            .styleModifier {
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
                property("display", "grid")
                property("grid-template-columns", "1fr 1fr")
                property("gap", "12px")
                property("align-content", "start")
            }
            .toAttrs()
    ) {
        clubs.forEach { club ->
            MobileClubCard(
                club = club,
                userLat = userLat,
                userLng = userLng,
                selectedCity = selectedCity,
                cityFlags = cityFlags,
                sportName = sportName,
                onClick = { onClubClick(club) }
            )
        }

        if (isLoadingMore) {
            Div(
                attrs = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("grid-column", "1 / -1")
                        property("text-align", "center")
                        property("padding", "16px")
                    }
                    .toAttrs()
            ) {
                // Intentionally empty — spacer only, no loading text (user pref).
            }
        }
    }
}

@Composable
private fun MobileClubCard(
    club: ClubDto,
    userLat: Double? = null,
    userLng: Double? = null,
    selectedCity: String? = null,
    cityFlags: Map<String, String> = emptyMap(),
    sportName: String = "all",
    onClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val imageUrl = club.mainPhotoUrl ?: "/icons/event_football_field.png"
    // Distance from the user when a city is picked, else the club's city
    // (street/address fallback when coords are missing).
    val locationText = com.arena_web.pages.clubLocationText(t, club, userLat, userLng, selectedCity)
    // Country flag replaces the geo pin in all-cities mode (mobile parity).
    // Derived from the club's own country (data-first), rendered as a local SVG.
    val flag = if (selectedCity == null) {
        com.arena_web.utils.flagAssetUrl(com.arena_web.pages.clubCountryOf(club))
    } else ""
    // Sport chip only while browsing all sports / Other (mobile parity).
    val sportLabel = if (sportName == "all" || sportName == "other") {
        com.arena_web.pages.clubSportLabel(club)
    } else null

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(200.px)
            .overflow(Overflow.Hidden)
            .borderRadius(12.px)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property("background",
                    "url('$imageUrl') center/cover no-repeat padding-box, " +
                    "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
            }
    ) {
        // Bottom gradient overlay
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(120.px)
                .position(Position.Absolute)
                .bottom(0.px).left(0.px)
                .styleModifier {
                    property("background", "linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.8) 100%)")
                }
        )

        // Rating badge — glass chip top-left over the photo (mobile
        // MobileClubCard parity).
        if (club.averageRating > 0) {
            Row(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(6.px).left(6.px)
                    .borderRadius(12.px)
                    .padding(topBottom = 2.px, leftRight = 6.px)
                    .gap(2.px)
                    .styleModifier {
                        property("position", "relative")
                        property("background", "rgba(0,0,0,0.5)")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Gradient border ring — inline (fixed px, this file doesn't
                // use the --scale var the shared GradientBorderRing relies on).
                Box(
                    modifier = Modifier.styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", "12px")
                        property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
                )
                // Brand-blue rating star (mobile parity) — recolor the gold
                // SVG via a mask so we don't need a separate asset.
                Box(
                    modifier = Modifier.size(14.px).styleModifier {
                        property("background-color", "#0061FF")
                        property("mask-image", "url('${AppIcons.StarRating16}')")
                        property("-webkit-mask-image", "url('${AppIcons.StarRating16}')")
                        property("mask-size", "contain")
                        property("-webkit-mask-size", "contain")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-repeat", "no-repeat")
                        property("mask-position", "center")
                        property("-webkit-mask-position", "center")
                    }
                )
                SpanText(
                    club.averageRating.asDynamic().toFixed(1) as String,
                    modifier = Modifier
                        .fontSize(12.px)
                        .fontWeight(FontWeight.Medium)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }

        // Club info at bottom — name, then flag/pin + location | sport
        // (mobile MobileClubCard parity).
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .bottom(0.px).left(0.px)
                .padding(12.px)
                .gap(4.px)
        ) {
            SpanText(
                club.name,
                modifier = Modifier
                    .fontSize(14.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(AppColors.PrimaryWhite)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
            )
            Row(
                modifier = Modifier.gap(4.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (flag.isNotBlank()) {
                    // Local flag SVG URL — small emoji-style 4:3 rounded-rect
                    // badge in place of the pin.
                    Box(
                        modifier = Modifier
                            .width(14.px)
                            .height(10.5.px)
                            .styleModifier {
                                property("background-image", "url(\"$flag\")")
                                property("background-size", "cover")
                                property("background-position", "center")
                                property("border-radius", "2px")
                            }
                    )
                } else {
                    Icon(
                        src = AppIcons.Location,
                        modifier = Modifier.size(12.px)
                    )
                }
                SpanText(
                    locationText,
                    modifier = Modifier
                        .fontSize(12.px)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                )
                if (sportLabel != null) {
                    Box(
                        modifier = Modifier
                            .margin(leftRight = 4.px)
                            .width(1.px).height(12.px)
                            .styleModifier {
                                property("background", "rgba(255,255,255,0.4)")
                                property("flex-shrink", "0")
                            }
                    )
                    SpanText(
                        sportLabel,
                        modifier = Modifier
                            .fontSize(12.px)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Mobile Empty States
// =============================================================================

@Composable
private fun MobileEmptyState(onAddEvent: () -> Unit = {}) {
    // Figma 10567:190928 typography & sizes; top padding 100 px (user pref).
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .mPaddingTop(100)
            .mGap(28),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().mGap(8),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Title — Body/2XL/M (Urbanist Medium 22/26, ls 0.22).
            SpanText(
                t.arenaEmptyTitle,
                modifier = Modifier
                    .mFontSize(22)
                    .fontWeight(500)
                    .mLineHeight(26)
                    .mLetterSpacing(0.22)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("white-space", "nowrap") }
            )
            // Subtitle — Body/L/R (Urbanist Regular 16/24 = lh 1.5, ls 0.16).
            SpanText(
                t.arenaEmptySubtitle,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(400)
                    .mLineHeight(24)
                    .mLetterSpacing(0.16)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .textAlign(TextAlign.Center)
            )
        }

        // Add Event button — 180×44, primary fill, radius 22, padding 24×10.
        Box(
            modifier = Modifier
                .mWidth(180)
                .mHeight(44)
                .mBorderRadius(22)
                .mPadding(10, 24)
                .backgroundColor(sportColors.primary)
                .cursor(Cursor.Pointer)
                .onClick { onAddEvent() },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.arenaAddEvent,
                modifier = Modifier
                    .mFontSize(16)
                    .fontWeight(500)
                    .mLineHeight(18)
                    .mLetterSpacing(0.032)
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun MobileEmptyClubsState() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.gap(20.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(72.px)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Location, modifier = Modifier.size(32.px))
            }

            Column(
                modifier = Modifier.gap(6.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    t.arenaNoClubsTitle,
                    modifier = Modifier
                        .fontSize(18.px)
                        .fontWeight(FontWeight.SemiBold)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                )
                SpanText(
                    t.arenaNoClubsSubtitle,
                    modifier = Modifier
                        .fontSize(14.px)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        }
    }
}
